import { useApolloClient } from '@apollo/client'
import type { FiltersDefinition, InFilterDefinition } from '@factorialco/f0-react'
import {
  EmployeeEmployeesFilterDocument,
  EmployeeEmployeesFilterQuery,
  EmployeeLegalEntitiesFilterDocument,
  EmployeeTeamsFilterDocument,
  EmployeeWorkplacesFilterDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

// Employee connection helpers
const extractEmployeeConnection = (data: EmployeeEmployeesFilterQuery) =>
  data.employees.employeeNamesConnection

const extractEmployeeNodes = (connection: ReturnType<typeof extractEmployeeConnection>) =>
  connection.edges.map((edge) => edge.node)

type EmployeeNode = NonNullable<ReturnType<typeof extractEmployeeNodes>>[number]

// Location filter - fetches all at once, no pagination
const useLocationFilter = (): InFilterDefinition<number> => {
  const client = useApolloClient()

  return {
    label: i18n.t('posts.dynamic_team_audience.filters.location'),
    type: 'in' as const,
    options: {
      cache: true,
      options: async () => {
        const { data } = await client.query({
          query: EmployeeWorkplacesFilterDocument,
          fetchPolicy: 'cache-first',
        })

        return data.locations.locations.map((location) => ({
          label: location.name,
          value: location.id,
        }))
      },
    },
  }
}

// Team filter - fetches all at once, no pagination
const useTeamFilter = (): InFilterDefinition<number> => {
  const client = useApolloClient()

  return {
    label: i18n.t('posts.dynamic_team_audience.filters.team'),
    type: 'in' as const,
    options: {
      cache: true,
      options: async () => {
        const { data } = await client.query({
          query: EmployeeTeamsFilterDocument,
          fetchPolicy: 'cache-first',
        })

        return data.teams.teams.map((team) => ({
          label: team.name,
          value: team.id,
        }))
      },
    },
  }
}

// Legal entity filter - fetches all at once, no pagination
const useLegalEntityFilter = (): InFilterDefinition<number> => {
  const client = useApolloClient()

  return {
    label: i18n.t('posts.dynamic_team_audience.filters.legal_entity'),
    type: 'in' as const,
    options: {
      cache: true,
      options: async () => {
        const { data } = await client.query({
          query: EmployeeLegalEntitiesFilterDocument,
          fetchPolicy: 'cache-first',
        })

        return data.companies.legalEntities.map((legalEntity) => ({
          label: legalEntity.legalName,
          value: legalEntity.id,
        }))
      },
    },
  }
}

// Employee data source - uses infinite scroll pagination
const useEmployeeDataSource = () => {
  const { company } = useCurrentSession()
  const companyId = company.id

  return useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      document: EmployeeEmployeesFilterDocument,
      perPage: 25,
      mapData: (data) => {
        const connection = extractEmployeeConnection(data)
        return extractEmployeeNodes(connection)
      },
      mapPagination: (data) => {
        const connection = extractEmployeeConnection(data)
        const pageInfo = connection.pageInfo
        const hasNextPage = !!pageInfo.hasNextPage
        const total = connection.totalCount ?? 0

        return {
          total,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({
        search,
        companyId,
      }),
    },
    [companyId]
  )
}

const useDirectReportsOfFilter = (): InFilterDefinition<number> => {
  const source = useEmployeeDataSource()

  return {
    label: i18n.t('posts.dynamic_team_audience.filters.direct_reports_of'),
    type: 'in' as const,
    options: {
      source,
      mapOptions: (employee: EmployeeNode) => ({
        label: employee.fullName,
        value: employee.id,
      }),
    },
  }
}

const useAllReporteesOfFilter = (): InFilterDefinition<number> => {
  const source = useEmployeeDataSource()

  return {
    label: i18n.t('posts.dynamic_team_audience.filters.all_reportees_of'),
    type: 'in' as const,
    options: {
      source,
      mapOptions: (employee: EmployeeNode) => ({
        label: employee.fullName,
        value: employee.id,
      }),
    },
  }
}

export interface DynamicAudienceFilters extends FiltersDefinition {
  location: InFilterDefinition<number>
  team: InFilterDefinition<number>
  legal_entity: InFilterDefinition<number>
  direct_reports_of: InFilterDefinition<number>
  all_reportees_of: InFilterDefinition<number>
}

export const useDynamicAudienceFilters = (): DynamicAudienceFilters => {
  const locationFilter = useLocationFilter()
  const teamFilter = useTeamFilter()
  const legalEntityFilter = useLegalEntityFilter()
  const directReportsOfFilter = useDirectReportsOfFilter()
  const allReporteesOfFilter = useAllReporteesOfFilter()

  return {
    location: locationFilter,
    team: teamFilter,
    legal_entity: legalEntityFilter,
    direct_reports_of: directReportsOfFilter,
    all_reportees_of: allReporteesOfFilter,
  }
}
